// Exports
// -------

// Utility for throttling repeated calls to a given function (i.e. execute the function once per
// delay period). This implementation borrowed heavily from [Underscore's throttle function](http://underscorejs.org/#throttle)
//
// Args:
//  1. `fn` - Function to be throttled
//  2. `delay` - How long to delay function execution in milliseconds
//  3. `executeImmediately` - Flag to indicate whether or not to execute the `fn` immediately (first
//     run)
//  4. `allowTrailingExecution` - Flag to indicate whether or not to allow the execution of the `fn`
//     on the trailing edge (i.e. remaining time is less than the specified delay)
//
// Returns: Throttled function
//
// Usage:
//  ```javascript
//  import throttle from 'throttle';
//  $(window).scroll(throttle(doSomethingNifty, 100));
//  ```
export default function _throttle(
  fn,
  delay,
  executeImmediately = true,
  allowTrailingExecution = true) {

  var previous = 0;
  var context;
  var args;
  var result;
  var timeout;

  // Function that gets executed on timeout
  function later() {
    previous = !executeImmediately ? 0 : new Date().getTime();
    timeout = null;
    result = fn.apply(context, args);

    if (!timeout) {
      context = args = null;
    }
  }

  // Throttled function
  return function() {

    const now = new Date().getTime();

    var remaining = delay - (now - previous);

    if (!previous && !executeImmediately) {
      previous = now;
    }

    context = this;
    args = arguments;

    if (remaining <= 0 || remaining > delay) {

      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }

      previous = now;
      result = fn.apply(context, args);

      if (!timeout) {
        context = args = null;
      }
    } else if (!timeout && allowTrailingExecution) {
      timeout = setTimeout(later, remaining);
    }

    return result;
  };
}



/** WEBPACK FOOTER **
 ** ./~/vt-frontend-engine/app/scripts/principles/performance/throttle.js
 **/